import { createFileRoute, useNavigate, Link } from '@tanstack/react-router';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import type { AuthResponse } from '@/features/auth/services/auth.service';
import { LoginForm } from '@/features/auth/components/LoginForm';
import { useAuthStore } from '@/store/auth.store';
import { useBrandingStore } from '@/store/branding.store';
import { platformSettingsService } from '@/features/branding/services/platform-settings.service';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';

export const Route = createFileRoute('/login')({
  component: LoginPage,
});

function LoginPage() {
  const navigate = useNavigate();
  const setAuth = useAuthStore((state) => state.setAuth);
  const queryClient = useQueryClient();
  const branding = useBrandingStore((s) => s.branding);

  // Usa o cache aquecido pelo __root.tsx — sem request extra
  const { data: publicSettings } = useQuery({
    queryKey: ['public-platform-settings'],
    queryFn: () => platformSettingsService.getPublic(),
    staleTime: 60_000,
    retry: false,
  });
  const signupEnabled = publicSettings?.public_signup_enabled !== false;

  const onLogin = (response: AuthResponse) => {
    // Limpar cache do React Query e dados residuais do usuário anterior
    queryClient.clear();
    localStorage.removeItem('token');
    localStorage.removeItem('auth-storage');
    localStorage.removeItem('user');

    setAuth(response.user, response.accessToken);
    toast.success('Login realizado com sucesso!');
    navigate({ to: '/my-dashboard' });
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-page-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <div className="mb-3 flex justify-center">
            <img
              src={branding?.logoUrl ?? '/assets/favicon-128x128.png'}
              alt={`Logo ${branding?.appName ?? 'Asseto'}`}
              className="h-14 w-14 rounded-xl object-contain"
            />
          </div>
          <CardTitle className="text-center">{branding?.appName ?? 'Asseto'}</CardTitle>
          <CardDescription>Faça login para acessar o sistema</CardDescription>
        </CardHeader>
        <CardContent>
          <LoginForm onLogin={onLogin}>
            {signupEnabled && (
              <p className="text-center text-sm text-muted-foreground">
                Não tem conta?{' '}
                <Link to="/register" className="text-primary hover:underline">
                  Criar conta grátis
                </Link>
              </p>
            )}
          </LoginForm>
        </CardContent>
      </Card>
    </div>
  );
}
